import { useEffect, useState } from "react";

/* ------------------------------------------------------------------ *
 * Shared driver profile photo — one face avatar per driver login, stored via
 * driver-photos-sb (settings KV). Reuse everywhere a driver's profile shows:
 * the depot floor plan, the driver portal (self-service), the Drivers admin
 * roster, Personnel, etc.
 *
 * Presentational + optional inline upload. It does NOT fetch in bulk — a parent
 * that renders a list should batch-load with fetchDriverPhotos() and pass each
 * `photo` in; a single-avatar caller can pass photo={undefined} and fetch one
 * with fetchDriverPhoto().
 * ------------------------------------------------------------------ */

const API = "/.netlify/functions/driver-photos-sb";

/** Downscale a picked image to a small square-ish avatar data URL. */
export function fileToAvatar(file, maxEdge = 320, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export async function setDriverPhoto(login, dataUrl) {
  const r = await fetch(API, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "setPhoto", login, dataUrl }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j.success === false) throw new Error(j.error || "Save failed");
  return true;
}

export async function fetchDriverPhoto(login) {
  try {
    const r = await fetch(API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "getPhoto", login }),
    });
    const j = await r.json();
    return (j && j.dataUrl) || "";
  } catch {
    return "";
  }
}

/** Batch { login: dataUrl } for logins that have a photo. */
export async function fetchDriverPhotos(logins) {
  const list = [...new Set((logins || []).filter(Boolean))];
  if (!list.length) return {};
  try {
    const r = await fetch(API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "getPhotos", logins: list }),
    });
    const j = await r.json();
    return (j && j.photos) || {};
  } catch {
    return {};
  }
}

/**
 * @param photo  data URL, '' (none), or undefined (loading → blank)
 * @param editable  show the tap-to-add/change overlay + file input
 * @param onUploaded(dataUrl)  called after a successful save (optimistic parents update here)
 */
export default function DriverAvatar({ login, photo, size = 40, editable = false, onUploaded, title, ring }) {
  const [working, setWorking] = useState(false);
  const [over, setOver] = useState(false);
  const initials = (login || "?").replace(/[^A-Za-z0-9]/g, "").slice(0, 2).toUpperCase() || "?";

  const processFile = async (f) => {
    if (!f) return;
    if (!/^image\//.test(f.type || "")) {
      // eslint-disable-next-line no-alert
      alert("That's not an image file.");
      return;
    }
    setWorking(true);
    try {
      const url = await fileToAvatar(f);
      await setDriverPhoto(login, url);
      onUploaded && onUploaded(url);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err.message || "Upload failed");
    } finally {
      setWorking(false);
    }
  };
  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    processFile(f);
  };

  const box = {
    width: size, height: size, borderRadius: "50%", overflow: "hidden", position: "relative",
    flex: "0 0 auto", background: "#EEEDF2", display: "flex", alignItems: "center", justifyContent: "center",
    border: over ? "2px solid #1D9E75" : ring ? `2px solid ${ring}` : "1px solid rgba(0,0,0,0.12)",
    boxShadow: over ? "0 0 0 3px rgba(29,158,117,0.28)" : "none",
  };
  const inner = photo ? (
    <img src={photo} alt={login || ""} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
  ) : (
    <span style={{ fontSize: Math.max(9, Math.round(size * 0.34)), fontWeight: 700, color: "#5f5e5a", letterSpacing: "0.02em" }}>
      {photo === undefined ? "" : initials}
    </span>
  );

  if (!editable) return <span style={box} title={title || login}>{inner}</span>;

  return (
    <label
      style={{ ...box, cursor: "pointer" }}
      title={title || "Tap, or drag a photo here"}
      onDragOver={(e) => { e.preventDefault(); if (!over) setOver(true); }}
      onDragEnter={(e) => e.preventDefault()}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        processFile(f);
      }}
    >
      {inner}
      <span
        style={{
          position: "absolute", left: 0, right: 0, bottom: 0, textAlign: "center",
          fontSize: Math.max(6, Math.round(size * 0.17)), fontWeight: 700, color: "#fff",
          background: "rgba(0,0,0,0.44)", padding: "1px 0", textTransform: "uppercase", letterSpacing: "0.04em",
        }}
      >
        {working ? "…" : over ? "Drop" : photo ? "Edit" : "Add"}
      </span>
      <input type="file" accept="image/*" onChange={onFile} disabled={working} style={{ display: "none" }} />
    </label>
  );
}

/** Self-contained: fetches its own photo for `login` and allows the signed-in
 *  driver to change it. Use in the driver portal header. */
export function SelfAvatar({ login, size = 40, editable = true }) {
  const [photo, setPhoto] = useState(undefined);
  useEffect(() => {
    let live = true;
    if (login) fetchDriverPhoto(login).then((p) => { if (live) setPhoto(p); });
    else setPhoto(undefined);
    return () => { live = false; };
  }, [login]);
  return <DriverAvatar login={login} photo={photo} size={size} editable={editable} onUploaded={setPhoto} />;
}
